// src/data/regions/fishIQThailandSpecies.ts
// Thailand FishIQ starter pack.
// Curated editorial guidance only: likely species, habitat associations, and learning cues.
// No regulatory/science authority claims and no exact spot claims.

import type { FishIQSpeciesSeed } from '../../types';

const editorial = [{ title: 'Roddo Thailand regional seed v1', sourceType: 'editorial' as const }];

const seaSeed = (input: FishIQSpeciesSeed): FishIQSpeciesSeed => ({
  ...input,
  editorialSources: input.editorialSources || editorial,
  confidence: input.confidence || 'editorial',
});

export const THAILAND_FISHIQ_SPECIES_SEED: FishIQSpeciesSeed[] = [
  seaSeed({
    id: 'giant_snakehead',
    slug: 'giant-snakehead',
    commonName: 'Giant Snakehead',
    scientificName: 'Channa micropeltes',
    aliases: ['Toman', 'Pla Chado', 'Red Snakehead'],
    family: 'snakehead',
    category: 'freshwater',
    habitats: ['lake', 'reservoir', 'river', 'canal', 'pond'],
    regions: ['Thailand', 'Southeast Asia'],
    summary: 'A powerful freshwater ambush predator often associated with vegetation, flooded edges, coves, canals, and slower warm-water structure.',
    feedingProfile: { primaryPrey: ['fish', 'frogs', 'small aquatic animals'], notes: 'Roddo read: look for ambush cover, bait movement, surface disturbance, and low-light feeding windows.' },
    bestBaits: ['Topwater Frog', 'Buzzbait', 'Swimbait', 'Spinnerbait'],
    bestTechniques: ['Work weed edges', 'Cast around flooded cover', 'Pause around ambush pockets'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['dawn', 'dusk'],
    beginnerTips: ['Start near vegetation or quiet edges with signs of bait.', 'Use strong tackle around cover.'],
    advancedTips: ['Fan-cast likely ambush lanes before moving tight to cover.', 'Watch for fry-guarding or surface activity, but avoid overclaiming exact behavior from one sign.'],
    behaviorTags: ['ambush predator', 'territorial'],
    structureTags: ['vegetation', 'canals', 'flooded edges', 'ambush pockets'],
    depthPreference: 'shallow-to-mid cover, especially around vegetation, flooded edges, and calm ambush lanes',
    activeTimes: ['dawn', 'dusk'],
    keyTraits: ['ambush predator', 'warmwater cover', 'topwater potential'],
  }),
  seaSeed({
    id: 'striped_snakehead',
    slug: 'striped-snakehead',
    commonName: 'Striped Snakehead',
    scientificName: 'Channa striata',
    aliases: ['Pla Chon', 'Common Snakehead'],
    family: 'snakehead',
    category: 'freshwater',
    habitats: ['pond', 'canal', 'river', 'lake', 'marsh'],
    regions: ['Thailand', 'Philippines', 'Indonesia', 'Southeast Asia'],
    summary: 'A hardy freshwater predator often found around shallow vegetation, canals, ponds, rice-field edges, and slow warm water.',
    feedingProfile: { primaryPrey: ['small fish', 'frogs', 'insects'], notes: 'Roddo read: quiet cover, oxygen pockets, and shallow food lanes can matter more than open-water casting.' },
    bestBaits: ['Topwater Frog', 'Spinnerbait', 'Soft Plastic', 'Live Bait'],
    bestTechniques: ['Work shallow vegetation', 'Cast along canal edges', 'Slow-roll cover lanes'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['dawn', 'dusk', 'day'],
    beginnerTips: ['Target easy cover first: grass, drains, canals, and pond edges.', 'Slow down when fish are tucked into weeds.'],
    advancedTips: ['Use weedless presentations through the highest-percentage lanes.', 'Match retrieve speed to water clarity and cover density.'],
    behaviorTags: ['ambush predator', 'cover-oriented'],
    structureTags: ['vegetation', 'canals', 'pond edges', 'drains'],
    depthPreference: 'mostly shallow cover and slow-water edges, with deeper refuge nearby in heat or pressure',
    activeTimes: ['dawn', 'day', 'dusk'],
    keyTraits: ['snakehead', 'shallow cover', 'warmwater predator'],
  }),
  seaSeed({
    id: 'barramundi',
    slug: 'barramundi',
    commonName: 'Barramundi / Asian Sea Bass',
    scientificName: 'Lates calcarifer',
    aliases: ['Barramundi', 'Asian Sea Bass', 'Pla Kapong', 'Siakap'],
    family: 'latid',
    category: 'both',
    habitats: ['river', 'estuary', 'bay', 'inshore', 'canal'],
    regions: ['Thailand', 'Indonesia', 'Southeast Asia', 'Indo-Pacific'],
    summary: 'A brackish/freshwater-to-inshore predator often tied to current seams, drains, mangroves, bridges, and bait movement.',
    feedingProfile: { primaryPrey: ['baitfish', 'shrimp', 'prawns'], notes: 'Roddo read: moving water, bait lanes, and ambush structure are the first checks.' },
    bestBaits: ['Paddle Tail', 'Minnow Plug', 'Shrimp Imitation', 'Live Bait'],
    bestTechniques: ['Cast current seams', 'Work drains and mangroves', 'Slow-roll structure edges'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['dawn', 'dusk', 'night'],
    beginnerTips: ['Find current and bait before picking a lure.', 'Bridges, drains, and mangroves are practical starter reads.'],
    advancedTips: ['Cast slightly upstream/current-side of structure and let the lure enter the ambush lane.', 'Night lights can create bait funnels in urban water.'],
    behaviorTags: ['ambush predator', 'current-aware feeder'],
    structureTags: ['mangroves', 'drains', 'bridges', 'current seams'],
    depthPreference: 'edges, drains, and structure lanes from shallow to mid-depth depending on tide/current and light',
    activeTimes: ['dawn', 'dusk', 'night'],
    keyTraits: ['brackish predator', 'current seams', 'ambush structure'],
  }),
  seaSeed({
    id: 'mekong_giant_catfish',
    slug: 'mekong-giant-catfish',
    commonName: 'Mekong Giant Catfish',
    scientificName: 'Pangasianodon gigas',
    aliases: ['Pla Buek'],
    family: 'shark catfish',
    category: 'freshwater',
    habitats: ['river', 'reservoir'],
    regions: ['Thailand', 'Mekong Basin', 'Southeast Asia'],
    summary: 'A giant river catfish profile for education-first FishIQ reads around large rivers, deep water, current, and managed fisheries.',
    feedingProfile: { primaryPrey: ['plant material', 'algae', 'detritus'], notes: 'Roddo read: treat this as a cautious educational/venue-specific profile, not a casual catch prediction.' },
    bestBaits: ['Venue-appropriate bait', 'Plant-based bait', 'Dough Bait'],
    bestTechniques: ['Check local venue guidance', 'Fish large-river edges carefully', 'Use heavy tackle where legal and appropriate'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['day', 'dusk'],
    beginnerTips: ['Verify local rules, access, and fishery status before targeting.', 'Treat sightings and venue claims as low-confidence until confirmed locally.'],
    advancedTips: ['Big-river flow and access matter more than generic species presence.', 'Use this profile as education unless Roddo local signals are strong.'],
    behaviorTags: ['large river fish', 'bottom-oriented'],
    structureTags: ['deep channels', 'river edges', 'current breaks'],
    depthPreference: 'large-river channels, deeper runs, and managed fishery water where present',
    activeTimes: ['day', 'dusk'],
    keyTraits: ['giant catfish', 'large river', 'education-first'],
  }),
  seaSeed({
    id: 'giant_siamese_carp',
    slug: 'giant-siamese-carp',
    commonName: 'Giant Siamese Carp',
    scientificName: 'Catlocarpio siamensis',
    aliases: ['Pla Kraho'],
    family: 'cyprinid',
    category: 'freshwater',
    habitats: ['river', 'lake', 'reservoir'],
    regions: ['Thailand', 'Mekong Basin', 'Southeast Asia'],
    summary: 'A large freshwater carp profile for deeper rivers, lakes, reservoirs, and managed carp venues.',
    feedingProfile: { primaryPrey: ['plant material', 'fruit', 'detritus', 'invertebrates'], notes: 'Roddo read: bottom/forage lanes, quiet presentation, and venue rules drive the profile.' },
    bestBaits: ['Boilie', 'Dough Bait', 'Corn', 'Plant-based Bait'],
    bestTechniques: ['Bottom fish patiently', 'Pre-bait where allowed', 'Focus on travel lanes'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['day', 'dusk'],
    beginnerTips: ['Use this as a venue-aware profile; local rules and fishery type matter.', 'Patience and quiet setups beat constant recasting.'],
    advancedTips: ['Route interception can outfish random bottom soaking.', 'Watch for mud, bubbles, and repeatable feeding lanes.'],
    behaviorTags: ['bottom feeder', 'scent/taste oriented'],
    structureTags: ['channels', 'mud flats', 'deep edges'],
    depthPreference: 'bottom-oriented lanes, deeper edges, and calm feeding flats',
    activeTimes: ['day', 'dusk'],
    keyTraits: ['giant carp', 'bottom feeder', 'venue-aware'],
  }),
  seaSeed({
    id: 'julliens_golden_carp',
    slug: 'julliens-golden-carp',
    commonName: "Jullien's Golden Carp",
    scientificName: 'Probarbus jullieni',
    aliases: ['Isok Barb', 'Pla Eesok'],
    family: 'cyprinid',
    category: 'freshwater',
    habitats: ['river', 'reservoir'],
    regions: ['Thailand', 'Mekong Basin', 'Southeast Asia'],
    summary: 'A river carp/barb profile best treated as a cautious regional education layer around large rivers and managed waters.',
    feedingProfile: { primaryPrey: ['invertebrates', 'plant material', 'bottom forage'], notes: 'Roddo read: broad river presence does not equal targetability; keep the guidance humble and local-signal driven.' },
    bestBaits: ['Dough Bait', 'Corn', 'Worm', 'Local Natural Bait'],
    bestTechniques: ['Fish bottom seams', 'Check venue guidance', 'Use patient bottom presentations'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['day', 'dusk'],
    beginnerTips: ['Verify local rules and fishery status first.', 'Use this as a likely-profile cue, not a guarantee.'],
    advancedTips: ['Current edges and feeding lanes matter more than map labels.', 'Lean on local observations before raising confidence.'],
    behaviorTags: ['bottom feeder', 'river-oriented'],
    structureTags: ['river channels', 'current seams', 'deep edges'],
    depthPreference: 'larger river runs, channel edges, and bottom forage lanes',
    activeTimes: ['day', 'dusk'],
    keyTraits: ['large barb', 'river profile', 'education-first'],
  }),
  seaSeed({
    id: 'walking_catfish',
    slug: 'walking-catfish',
    commonName: 'Walking Catfish',
    scientificName: 'Clarias batrachus',
    aliases: ['Pla Duk', 'Hito'],
    family: 'airbreathing catfish',
    category: 'freshwater',
    habitats: ['pond', 'canal', 'river', 'marsh', 'creek'],
    regions: ['Thailand', 'Philippines', 'Indonesia', 'Southeast Asia'],
    summary: 'A hardy air-breathing catfish profile for ponds, canals, marshy edges, drains, and warm low-oxygen water.',
    feedingProfile: { primaryPrey: ['worms', 'insects', 'small fish', 'detritus'], notes: 'Roddo read: scent, bottom contact, night/low-light windows, and drains are practical cues.' },
    bestBaits: ['Worm', 'Cut Bait', 'Prepared Bait', 'Small Live Bait'],
    bestTechniques: ['Bottom fish drains', 'Target low-light periods', 'Work canal edges'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['dusk', 'night'],
    beginnerTips: ['Scent matters; keep bait near bottom.', 'Canals and pond drains are simple starter targets.'],
    advancedTips: ['Rain/runoff can reposition fish toward moving-water food lanes.', 'Use short, controlled casts around tight edges.'],
    behaviorTags: ['scent/taste oriented', 'bottom feeder'],
    structureTags: ['drains', 'canals', 'mud edges', 'marsh edges'],
    depthPreference: 'bottom-oriented shallow-to-mid drains, canals, and pond edges',
    activeTimes: ['dusk', 'night'],
    keyTraits: ['catfish', 'scent feeder', 'canal/pond profile'],
  }),
  seaSeed({
    id: 'nile_tilapia',
    slug: 'nile-tilapia',
    commonName: 'Nile Tilapia',
    scientificName: 'Oreochromis niloticus',
    aliases: ['Tilapia', 'Pla Nin'],
    family: 'cichlid',
    category: 'freshwater',
    habitats: ['pond', 'lake', 'reservoir', 'canal', 'river'],
    regions: ['Thailand', 'Philippines', 'Indonesia', 'Southeast Asia', 'Introduced Worldwide'],
    summary: 'A common warmwater tilapia profile for ponds, canals, reservoirs, and slow edges with algae, vegetation, and small forage.',
    feedingProfile: { primaryPrey: ['algae', 'plants', 'small invertebrates'], notes: 'Roddo read: shallow edges, beds, vegetation, and small natural baits are first checks.' },
    bestBaits: ['Bread', 'Dough Bait', 'Corn', 'Worm'],
    bestTechniques: ['Float fish shallow edges', 'Use small hooks', 'Target warm calm pockets'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['day', 'dusk'],
    beginnerTips: ['Go small and subtle around visible fish.', 'Pond and canal edges are practical starting points.'],
    advancedTips: ['Clear-water fish can be spooky; reduce splash and line size.', 'Beds and feeding lanes can be repeatable.'],
    behaviorTags: ['schooling', 'bottom feeder'],
    structureTags: ['vegetation', 'beds', 'pond edges', 'canals'],
    depthPreference: 'shallow-to-mid warm edges, vegetation, and bed/feeding lanes',
    activeTimes: ['day', 'dusk'],
    keyTraits: ['tilapia', 'warmwater', 'beginner-friendly'],
  }),
  seaSeed({
    id: 'giant_trevally',
    slug: 'giant-trevally',
    commonName: 'Giant Trevally',
    scientificName: 'Caranx ignobilis',
    aliases: ['GT', 'Maliputo', 'Talakitok'],
    family: 'jack',
    category: 'saltwater',
    habitats: ['ocean', 'inshore', 'bay', 'estuary', 'surf'],
    regions: ['Thailand', 'Philippines', 'Indonesia', 'Southeast Asia', 'Indo-Pacific'],
    summary: 'A hard-pulling coastal predator tied to reef edges, current lines, points, bait schools, channels, and surf/inshore structure.',
    feedingProfile: { primaryPrey: ['baitfish', 'squid', 'crustaceans'], notes: 'Roddo read: current plus bait plus structure is the key stack.' },
    bestBaits: ['Popping Plug', 'Stickbait', 'Metal Jig', 'Live Bait'],
    bestTechniques: ['Cast current edges', 'Work reef points', 'Run-and-gun bait activity'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['dawn', 'day', 'dusk'],
    beginnerTips: ['Find current and bait before grinding one dead shoreline.', 'Use tackle sized for hard runs near structure.'],
    advancedTips: ['Cast across current seams and let the lure cross ambush lanes.', 'Tide change can move fish from reef edges into channels.'],
    behaviorTags: ['roaming predator', 'bait-tracker'],
    structureTags: ['reef edges', 'current lines', 'points', 'channels'],
    depthPreference: 'surface-to-mid water around current, reef edges, channels, and bait schools',
    activeTimes: ['dawn', 'day', 'dusk'],
    keyTraits: ['GT', 'current lines', 'reef predator'],
  }),
  seaSeed({
    id: 'queenfish',
    slug: 'queenfish',
    commonName: 'Queenfish',
    scientificName: 'Scomberoides commersonnianus',
    aliases: ['Talang Queenfish'],
    family: 'leatherjacket',
    category: 'saltwater',
    habitats: ['inshore', 'bay', 'estuary', 'ocean', 'surf'],
    regions: ['Thailand', 'Southeast Asia', 'Indo-Pacific'],
    summary: 'A fast inshore predator often found around beaches, river mouths, bait schools, and current-washed edges.',
    feedingProfile: { primaryPrey: ['small baitfish', 'shrimp'], notes: 'Roddo read: watch for surface activity, current lanes, and bait flickers.' },
    bestBaits: ['Metal Jig', 'Small Plug', 'Pencil Bait', 'Soft Plastic'],
    bestTechniques: ['Cast to surface activity', 'Work beach gutters', 'Retrieve quickly through bait lanes'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['dawn', 'day', 'dusk'],
    beginnerTips: ['Keep a fast-cast lure ready when bait starts moving.', 'Beaches and river-mouth edges are strong starter reads.'],
    advancedTips: ['Vary speed until followers commit.', 'Use lighter metals when fish are chasing small bait.'],
    behaviorTags: ['schooling', 'bait-tracker'],
    structureTags: ['beach gutters', 'river mouths', 'current lines'],
    depthPreference: 'surface-to-mid inshore bait lanes, beaches, and river-mouth edges',
    activeTimes: ['dawn', 'day', 'dusk'],
    keyTraits: ['fast inshore predator', 'surface activity', 'bait schools'],
  }),
  seaSeed({
    id: 'snapper',
    slug: 'snapper',
    commonName: 'Snapper',
    scientificName: 'Lutjanus spp.',
    aliases: ['Lutjanid Snapper'],
    family: 'snapper',
    category: 'saltwater',
    habitats: ['reef', 'inshore', 'bay', 'estuary', 'ocean'] as any,
    regions: ['Thailand', 'Philippines', 'Indonesia', 'Southeast Asia', 'Indo-Pacific'],
    summary: 'A broad reef/inshore snapper profile for structure, mangroves, reefs, rocks, channels, and current-fed feeding windows.',
    feedingProfile: { primaryPrey: ['baitfish', 'shrimp', 'crabs', 'squid'], notes: 'Roddo read: structure plus current is more useful than a generic coastline label.' },
    bestBaits: ['Shrimp', 'Squid', 'Jig', 'Live Bait'],
    bestTechniques: ['Fish structure edges', 'Work current-fed rocks', 'Use natural bait near bottom'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['dawn', 'dusk', 'night'],
    beginnerTips: ['Stay close to structure but manage snags.', 'Moving water often improves the read.'],
    advancedTips: ['Use leader abrasion resistance near rocks and reef.', 'Night and low-light windows can raise confidence.'],
    behaviorTags: ['ambush predator', 'scent/taste oriented'],
    structureTags: ['reefs', 'rocks', 'mangroves', 'channels'],
    depthPreference: 'near-bottom to mid-depth around reefs, rocks, mangroves, and channel edges',
    activeTimes: ['dawn', 'dusk', 'night'],
    keyTraits: ['structure fish', 'current-fed', 'reef/inshore'],
  }),
  seaSeed({
    id: 'grouper',
    slug: 'grouper',
    commonName: 'Grouper',
    scientificName: 'Epinephelus spp.',
    aliases: ['Rock Cod', 'Lapu-lapu'],
    family: 'grouper',
    category: 'saltwater',
    habitats: ['reef', 'inshore', 'bay', 'ocean'] as any,
    regions: ['Thailand', 'Philippines', 'Indonesia', 'Southeast Asia', 'Indo-Pacific'],
    summary: 'A structure-bound ambush profile for reefs, rocks, holes, ledges, and heavy cover in tropical inshore/offshore water.',
    feedingProfile: { primaryPrey: ['baitfish', 'crabs', 'squid'], notes: 'Roddo read: exact structure and first-drop accuracy matter more than broad area presence.' },
    bestBaits: ['Live Bait', 'Jig', 'Squid', 'Swimbait'],
    bestTechniques: ['Drop tight to structure', 'Use strong tackle', 'Work ledges and holes'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['day', 'dusk'],
    beginnerTips: ['Expect hard dives into structure.', 'Fish close enough to cover to be in the strike zone.'],
    advancedTips: ['Boat angle/current angle controls whether the bait reaches the hole.', 'Short leaders and heavy drag may be needed around sharp reef.'],
    behaviorTags: ['ambush predator', 'structure-bound'],
    structureTags: ['reefs', 'holes', 'ledges', 'rocks'],
    depthPreference: 'near-bottom around reef holes, ledges, rock piles, and heavy structure',
    activeTimes: ['day', 'dusk'],
    keyTraits: ['reef ambush fish', 'heavy structure', 'bottom-oriented'],
  }),
  seaSeed({
    id: 'narrow_barred_spanish_mackerel',
    slug: 'narrow-barred-spanish-mackerel',
    commonName: 'Narrow-barred Spanish Mackerel',
    scientificName: 'Scomberomorus commerson',
    aliases: ['Spanish Mackerel', 'Tanigue', 'Narrow-barred Mackerel'],
    family: 'mackerel',
    category: 'saltwater',
    habitats: ['ocean', 'inshore', 'bay', 'surf'],
    regions: ['Thailand', 'Indonesia', 'Philippines', 'Southeast Asia', 'Indo-Pacific'],
    summary: 'A fast coastal pelagic profile tied to bait schools, reefs, headlands, channels, and clean moving water.',
    feedingProfile: { primaryPrey: ['baitfish', 'squid'], notes: 'Roddo read: speed, flash, current, and bait are the practical cues.' },
    bestBaits: ['Metal Jig', 'Trolling Lure', 'Spoon', 'Live Bait'],
    bestTechniques: ['Troll bait edges', 'Cast fast metals', 'Work headlands and channels'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['dawn', 'day', 'dusk'],
    beginnerTips: ['Use wire or bite-resistant leader where cutoffs happen.', 'Watch birds and bait schools.'],
    advancedTips: ['Match lure size to bait size before changing locations.', 'Current-washed points can reload during moving tide.'],
    behaviorTags: ['bait-tracker', 'roaming predator'],
    structureTags: ['headlands', 'channels', 'reef edges', 'bait schools'],
    depthPreference: 'surface-to-mid water around bait schools, channels, reef edges, and current lines',
    activeTimes: ['dawn', 'day', 'dusk'],
    keyTraits: ['fast pelagic', 'toothy', 'bait schools'],
  }),
  seaSeed({
    id: 'sailfish',
    slug: 'sailfish',
    commonName: 'Sailfish',
    scientificName: 'Istiophorus platypterus',
    aliases: ['Indo-Pacific Sailfish'],
    family: 'billfish',
    category: 'saltwater',
    habitats: ['ocean', 'inshore'],
    regions: ['Thailand', 'Philippines', 'Indonesia', 'Southeast Asia', 'Indo-Pacific'],
    summary: 'An offshore billfish profile associated with bait, current edges, rips, FAD-like structure, and bluewater trolling/casting windows.',
    feedingProfile: { primaryPrey: ['baitfish', 'squid'], notes: 'Roddo read: offshore bait/current context is required before confidence should rise.' },
    bestBaits: ['Trolling Lure', 'Live Bait', 'Ballyhoo-style Bait', 'Pitch Bait'],
    bestTechniques: ['Troll current edges', 'Watch birds and bait', 'Pitch to raised fish'],
    bestSeasons: ['spring', 'summer', 'fall'],
    bestTimesOfDay: ['day', 'dawn'],
    beginnerTips: ['Treat this as an offshore/charter-style profile, not a shoreline prediction.', 'Bait and water movement matter more than generic ocean presence.'],
    advancedTips: ['Use temperature/color/current edges where available.', 'Keep a pitch bait ready when fish appear behind teasers or lures.'],
    behaviorTags: ['roaming predator', 'bait-tracker'],
    structureTags: ['current edges', 'rips', 'bluewater', 'bait schools'],
    depthPreference: 'open-water surface-to-mid bait zones and current edges',
    activeTimes: ['day', 'dawn'],
    keyTraits: ['billfish', 'offshore', 'bait/current dependent'],
  }),
];
